import Num, { BigNumber } from '@flipster/utils/num';

import type { OrderInput } from '.';
import type { InternalPosition } from './internal-types';
import { toFiniteDecimal, toNonNegativeDecimal, toPositiveDecimal } from './number-guards';

const isSameDirection = (a: BigNumber, b: BigNumber) => (a.gt(0) && b.gt(0)) || (a.lt(0) && b.lt(0));

/**
 * Simulates the post-fill `Position` produced by applying `order` to
 * `targetPosition`. Covers Open / Increase / Reduce / Flip cases; returns
 * `null` for fully-closing orders (caller renders `—`).
 */
export function simulateOrderToPosition(
  targetPosition: InternalPosition | null,
  order: OrderInput,
  targetSymbol: string = '',
): InternalPosition | null {
  const qty = toPositiveDecimal(order.qty);
  const orderPrice = toPositiveDecimal(order.price);
  const leverage = toPositiveDecimal(String(order.initLeverage));
  const feeRate = toNonNegativeDecimal(order.feeRate);

  if (!qty || !orderPrice || !leverage || !feeRate) return null;

  const fee = qty.multipliedBy(orderPrice).multipliedBy(feeRate);
  const orderSign = order.side === 'Long' ? 1 : -1;
  const signedOrderQty = qty.multipliedBy(orderSign);
  const preSize = targetPosition ? toFiniteDecimal(targetPosition.position) : Num('0');
  const preEntryPrice = targetPosition ? toPositiveDecimal(targetPosition.avgEntryPrice) : Num('0');
  const preMarkPrice = targetPosition ? toPositiveDecimal(targetPosition.markPrice) : orderPrice;

  if (!preSize || !preEntryPrice || !preMarkPrice) return null;

  const postSize = preSize.plus(signedOrderQty);

  if (postSize.eq(0)) return null;

  const sameDirection = targetPosition ? isSameDirection(preSize, signedOrderQty) : false;
  const isReduce = !!targetPosition && !sameDirection && qty.lt(preSize.abs());
  const isFlip = !!targetPosition && !sameDirection && !isReduce;
  let postEntryPrice: BigNumber;

  if (!targetPosition || isFlip) {
    postEntryPrice = orderPrice;
  } else if (sameDirection) {
    postEntryPrice = preSize
      .multipliedBy(preEntryPrice)
      .plus(signedOrderQty.multipliedBy(orderPrice))
      .dividedBy(postSize);
  } else {
    postEntryPrice = preEntryPrice;
  }

  let isolatedMarginInitial: string = '0';
  let isolatedMarginAdded: string | undefined = undefined;
  let walletRealization = '0';

  if (order.marginMode === 'Isolated') {
    const targetInitial = targetPosition ? toNonNegativeDecimal(targetPosition.isolatedMarginInitial) : Num('0');
    const targetAdded = targetPosition ? toNonNegativeDecimal(targetPosition.isolatedMarginAdded ?? '0') : Num('0');
    let initial: BigNumber;

    if (!targetInitial || !targetAdded) return null;

    if (!targetPosition || isFlip) {
      initial = postSize.abs().multipliedBy(orderPrice).dividedBy(leverage).minus(fee);
    } else if (sameDirection) {
      initial = targetInitial.plus(qty.multipliedBy(orderPrice).dividedBy(leverage)).minus(fee);
    } else {
      initial = targetInitial.multipliedBy(postSize.abs()).dividedBy(preSize.abs()).minus(fee);
    }

    isolatedMarginInitial = initial.toString();
    isolatedMarginAdded = sameDirection || isReduce ? targetAdded.toString() : '0';
  } else {
    const slip = signedOrderQty.multipliedBy(orderPrice.minus(preMarkPrice));
    walletRealization = fee.negated().minus(slip).toString();
  }

  return {
    positionId: targetPosition?.positionId ?? '',
    symbol: targetPosition?.symbol ?? targetSymbol,
    position: postSize.toString(),
    avgEntryPrice: postEntryPrice.toString(),
    marginType: order.marginMode,
    initLeverage: order.initLeverage,
    isolatedMarginInitial,
    isolatedMarginAdded,
    markPrice: (targetPosition ? preMarkPrice : orderPrice).toString(),
    walletRealization,
  };
}
